import { MouseEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useProvider, ToastVariant, useToastMessage } from '~/Hooks';
import { useLocationListWithStatus } from '~/Hooks/useLocationsList';
import { IntegrationValues } from '~/Models/DTO/Integration.model';
import { useTranslation } from 'react-i18next';
import {
  IEversportsChannelById,
  IIntegrationChannelById,
  IVirtuaGymChannelById,
  IZingfitChannelById,
  IVirtuaGymV1ChannelById,
} from '~/Models/DTO/IntegrationChannels.model';
import { ILocationWithStatus } from '~/Models/interfaces/Location.model';
import { useRequestChannels } from '~/requests/adapterSvc/useRequestChannels';
import { useRequestPostChannel } from '~/requests/adapterSvc/useRequestPostChannel';
import { useRequestPostEversportsChannel } from '~/requests/adapterSvc/useRequestPostEversportsChannel';
import { useRequestPostVirtuaGymChannel } from '~/requests/adapterSvc/useRequestPostVirtuaGymChannel';
import { useRequestPostZingfitChannel } from '~/requests/adapterSvc/useRequestPostZingfitChannel';
import { useRequestPostVirtuaGymV1Channel } from '~/requests/adapterSvc/useRequestPostVirtuaGymV1Channel';
import { useRequestUpdateChannel } from '~/requests/adapterSvc/useRequestUpdateChannel';
import { useRequestPostMindbodyChannel } from '~/requests/adapterSvc/useRequestPostMindbodyChannel';
import { useRequestPostResamaniaChannel } from '~/requests/adapterSvc/useRequestPostResamaniaChannel';
interface UseChannelSetupProps {
  channel:
    | IIntegrationChannelById
    | IEversportsChannelById
    | IVirtuaGymChannelById
    | IZingfitChannelById
    | IVirtuaGymV1ChannelById
    | undefined;
  mode: 'new' | 'edit';
  integrationType: string;
  id: string;
}
export interface State {
  anchorEl: HTMLElement | null;
  expandCard: boolean;
  selectedLocation: string;
  selectedLocationName: string;
  startDate: string;
  endDate: string;
  initialStartDate: string;
  initialEndDate: string;
  oneFitPartnerId: string;
  baseUrl: string;
  apiKey: string;
  webHookKey: string;
  venueId: string;
  livestream: boolean;
  venueUUID: string;
  clientId: string;
  clientSecret: string;
  clubId: string;
  paymentPlanId: string;
  tag: string;
  webhookSecret: string;
  agreementId: string;
  initialProceed: boolean;
  isLoading: boolean;
  eversportsLoading: boolean;
  updateChannelLoading: boolean;
  locationsLoading: boolean;
  locationsError?: any;
  error?: any;
  eversportsError?: any;
  virtuaGymError?: any;
  zingfitError?: any;
  mindbodyError?: any;
  virtuaGymLoading: boolean;
  zingfitLoading: boolean;
  mindbodyLoading: boolean;
  virtuaGymV1Loading: boolean;
  virtuaGymV1Error?: any;
  updateChannelError?: any;
  channelUpdated?: boolean;
  locations?: ILocationWithStatus[];
  url: string;
  regionId: string;
  siteId: string;
  activationCode: string;
  endpoint?: string;
  accessToken?: string;
  clubKey: string;
  scheduleId: string;
  locationId: string;
  serviceId: string;
  countryCode: string;
  studioId: string;
  clubID: string;
  networkId: string;
  clientToken: string;
}

export interface Actions {
  handleToggleDetails(): void;
  handleOpenPopover(event: MouseEvent<HTMLElement>): void;
  handleClosePopover(): void;
  handleDateChange(name: string, date: any): void;
  clearEndDate(): void;
  handleSelectItem(location: string, locationName: string): void;
  handleSave(): void;
  setOneFitPartnerId(id: string): void;
  setBaseUrl(url: string): void;
  setApiKey(key: string): void;
  setWebHookKey(key: string): void;
  setVenueId(id: string): void;
  setLivestream(value: boolean): void;
  setVenueUUID(id: string): void;
  setClientId(id: string): void;
  setClientSecret(secret: string): void;
  setClubId(id: string): void;
  setPaymentPlanId(id: string): void;
  setTag(tag: string): void;
  setWebhookSecret(secret: string): void;
  setAgreementId(id: string): void;
  setUrl(url: string): void;
  setRegionId(regionId: string): void;
  setSiteId(siteId: string): void;
  setActivationCode(activationCode: string): void;
  setAccessToken(token: string): void;
  setEndpoint(endpoint: string): void;
  setScheduleId(id: string): void;
  setClubKey(secret: string): void;
  setLocationId(id: string): void;
  setServiceId(id: string): void;
  setCountryCode(code: string): void;
  setStudioId(id: string): void;
  setClubID(id: string): void;
  setNetworkId(id: string): void;
  setClientToken(token: string): void;
}

export const resetTimeToZero = (dateString: string): string => {
  const isoString = dateString.slice(0, 10);
  const result = `${isoString}T00:00:00Z`;

  return result;
};
export const useChannelSetup = ({ channel, mode, integrationType, id }: UseChannelSetupProps) => {
  const { currentProvider } = useProvider();
  const providerUUID = currentProvider?.uuid as string;
  const history = useHistory();
  const { showToast } = useToastMessage();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandCard, setExpandCard] = useState<boolean>(true);

  const [countryCode, setCountryCode] = useState<string>('');

  //  common states (careful, should be checked before removing them)
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedLocationName, setSelectedLocationName] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [initialStartDate, setInitialStartDate] = useState<string>('');
  const [initialEndDate, setInitialEndDate] = useState<string>('');
  const [oneFitPartnerId, setOneFitPartnerId] = useState<string>('');
  const [apiKey, setApiKey] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');

  // MagicLine specific states
  const [webHookKey, setWebHookKey] = useState<string>('');

  // Eversports specific states
  const [venueId, setVenueId] = useState<string>('null');
  const [livestream, setLivestream] = useState<boolean>(false);
  const [venueUUID, setVenueUUID] = useState<string>('null');
  const [endpoint, setEndpoint] = useState<string>('https://aggregator.eversports.io/v1/graphql');
  const [accessToken, setAccessToken] = useState<string>(
    'zueec1zDNylvebmJfvqcvYrwyjgtWCHbVBVWnkL2BrVdQqdGA6CAYX43VI3cM6IL',
  );

  // PerfectGym specific states
  const [clientSecret, setClientSecret] = useState<string>('');
  const [clubId, setClubId] = useState<string>('');
  const [paymentPlanId, setPaymentPlanId] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [webhookSecret, setWebhookSecret] = useState<string>('');
  const [agreementId, setAgreementId] = useState<string>('');

  // Zingfit specific states
  const [url, setUrl] = useState<string>('https://api.zingfit.com');
  const [regionId, setRegionId] = useState<string>('');
  const [siteId, setSiteId] = useState<string>('');
  const [activationCode, setActivationCode] = useState<string>('');

  // virtuagymV1 specific states
  const [clubKey, setClubKey] = useState<string>('');
  const [scheduleId, setScheduleId] = useState<string>('0');

  // mindbody specific states
  const [locationId, setLocationId] = useState<string>('0');
  const [serviceId, setServiceId] = useState<string>('');

  // resamania specific states
  const [studioId, setStudioId] = useState<string>('0');
  const [clubID, setClubID] = useState<string>('0');
  const [networkId, setNetworkId] = useState<string>('0');
  const [clientToken, setClientToken] = useState<string>('');

  const [initialProceed, setInitialProceed] = useState<boolean>(false);

  const { error, isLoading, mutate: postChannel } = useRequestPostChannel({
    location: selectedLocation,
  });

  const {
    error: eversportsError,
    isLoading: eversportsLoading,
    mutate: postEversportsChannel,
  } = useRequestPostEversportsChannel({
    location: selectedLocation,
  });

  const {
    error: virtuaGymError,
    isLoading: virtuaGymLoading,
    mutate: postVirtuaGymChannel,
  } = useRequestPostVirtuaGymChannel({
    location: selectedLocation,
  });

  const {
    error: zingfitError,
    isLoading: zingfitLoading,
    mutate: postZingfitChannel,
  } = useRequestPostZingfitChannel({
    location: selectedLocation,
  });

  const {
    error: virtuaGymV1Error,
    isLoading: virtuaGymV1Loading,
    mutate: postVirtuaGymV1Channel,
  } = useRequestPostVirtuaGymV1Channel({
    location: selectedLocation,
  });

  const {
    error: mindbodyError,
    isLoading: mindbodyLoading,
    mutate: postMindbodyChannel,
  } = useRequestPostMindbodyChannel({
    location: selectedLocation,
  });

  const {
    error: resamaniaError,
    isLoading: resamaniaLoading,
    mutate: postResamaniaChannel,
  } = useRequestPostResamaniaChannel({
    location: selectedLocation,
  });

  const {
    error: updateChannelError,
    isLoading: updateChannelLoading,
    isSuccess: channelUpdated,
    mutate: updateChannel,
  } = useRequestUpdateChannel({
    channelId: id,
  });

  const { refetch } = useRequestChannels({ providerId: providerUUID });

  const defaultQuery = { providerId: providerUUID || '' };

  const {
    data: locations,
    isLoading: locationsLoading,
    error: locationsError,
  } = useLocationListWithStatus(defaultQuery);

  const handleToggleDetails = () => {
    setExpandCard(!expandCard);
  };

  const handleOpenPopover = (e: MouseEvent<HTMLElement>) => {
    if (mode !== 'edit') {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (name: string, date: any) => {
    if (name === IntegrationValues.StartDate) {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const clearEndDate = () => {
    setEndDate('');
    setInitialEndDate('');
  };

  const handleSelectItem = (location: string, locationName: string) => {
    setSelectedLocation(location);
    setSelectedLocationName(locationName);

    const selected = locations?.find((loc) => loc.id === location);
    if (selected && selected.countryISOA2) {
      setCountryCode(selected.countryISOA2);
    }

    handleClosePopover();
  };

  const handleSave = () => {
    try {
      const commonSettings = {
        start_date: startDate?.length > 0 ? resetTimeToZero(startDate) : '',
        end_date:
          endDate?.length > 0
            ? resetTimeToZero(endDate)
            : initialEndDate?.length > 0
            ? resetTimeToZero(initialEndDate)
            : '',
        provider_id: providerUUID,
        country_code: countryCode || '',
      };

      let channelSettings: any = {};
      let mutateFunction;

      if (integrationType === 'magicline') {
        channelSettings = {
          ...commonSettings,
          integration: 'magicline',
          settings: [
            { key: 'api_key', value: apiKey },
            { key: 'webhook_key', value: webHookKey },
            { key: 'url', value: baseUrl },
          ],
        };
        mutateFunction = mode === 'new' ? postChannel : undefined;
      } else if (integrationType === 'eversports') {
        channelSettings = {
          ...commonSettings,
          integration: 'eversports',
          settings: [
            { key: 'location_uuid', value: locationId },
            { key: 'endpoint', value: endpoint },
            { key: 'access_token', value: accessToken },
            { key: 'venue_uuid', value: venueUUID },
            { key: 'livestream', value: livestream.toString() },
          ],
        };
        mutateFunction = mode === 'new' ? postEversportsChannel : undefined;
      } else if (integrationType === 'virtuagym') {
        channelSettings = {
          ...commonSettings,
          integration: 'virtuagym',
          settings: [{ key: 'club_id', value: clubId }],
        };
        mutateFunction = mode === 'new' ? postVirtuaGymChannel : undefined;
      } else if (integrationType === 'zingfit') {
        channelSettings = {
          ...commonSettings,
          integration: 'zingfit',
          settings: [
            { key: 'url', value: url },
            { key: 'client_id', value: clientId },
            { key: 'client_secret', value: clientSecret },
            { key: 'region_id', value: regionId },
            { key: 'site_id', value: siteId },
            { key: 'activation_code', value: activationCode },
          ],
        };
        mutateFunction = mode === 'new' ? postZingfitChannel : undefined;
      } else if (integrationType === 'virtuagymv1') {
        channelSettings = {
          ...commonSettings,
          integration: 'virtuagymv1',
          settings: [
            { key: 'club_id', value: clubId },
            { key: 'club_key', value: clubKey },
            { key: 'schedule_id', value: scheduleId },
          ],
        };
        mutateFunction = mode === 'new' ? postVirtuaGymV1Channel : undefined;
      } else if (integrationType === 'mindbody') {
        channelSettings = {
          ...commonSettings,
          integration: 'mindbody',
          settings: [
            { key: 'site_id', value: siteId },
            { key: 'location_id', value: locationId },
            { key: 'service_id', value: serviceId },
          ],
        };
        mutateFunction = mode === 'new' ? postMindbodyChannel : undefined;
      } else if (integrationType === 'resamania') {
        channelSettings = {
          ...commonSettings,
          integration: 'resamania',
          settings: [
            { key: 'country_code', value: countryCode },
            { key: 'studio_id', value: studioId },
            { key: 'club_id', value: clubID },
            { key: 'network_id', value: networkId },
            { key: 'client_token', value: clientToken },
          ],
        };
        mutateFunction = mode === 'new' ? postResamaniaChannel : undefined;
      }

      if (mode === 'new') {
        if (mutateFunction) {
          mutateFunction(channelSettings as any, {
            onSuccess: async (data) => {
              const refetchResult = await refetch();
              const updatedChannels = refetchResult.data;
              const newUrl = window.location.pathname.replace('new', data.id);
              setExpandCard(false);
              setStartDate('');
              if (updatedChannels) {
                history.push(newUrl);
              }
            },
          });
        }
      } else {
        updateChannel({
          start_date: channelSettings.start_date,
          end_date: channelSettings.end_date,
          country_code: countryCode,
          settings: channelSettings.settings,
        });
        setExpandCard(false);
        showToast('Magicline', t('magicline.cleanup.channel.success'), ToastVariant.SUCCESS, true);
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (channel && !initialProceed) {
      const matchingLocation = locations?.find((location) => location.id === channel?.location_id);

      if (matchingLocation) {
        setSelectedLocation(matchingLocation.id);
        setSelectedLocationName(matchingLocation.name);
        setInitialStartDate(channel.start_date);
        setStartDate(channel?.start_date || '');
        setInitialEndDate(channel.end_date);
        setCountryCode(matchingLocation.countryISOA2 || '');
        channel.settings.forEach((setting) => {
          switch (setting.key) {
            case 'url':
              setBaseUrl(setting.value);
              break;
            case 'api_key':
              setApiKey(setting.value);
              break;
            case 'webhook_key':
              setWebHookKey(setting.value);
              break;
            case 'venue_id':
              setVenueId(setting.value);
              break;
            case 'livestream':
              setLivestream(setting.value === 'true');
              break;
            case 'venue_uuid':
              setVenueUUID(setting.value ? setting.value : 'null');
              break;
            case 'client_id':
              setClientId(setting.value);
              break;
            case 'client_secret':
              setClientSecret(setting.value);
              break;
            case 'club_id':
              setClubId(setting.value);
              break;
            case 'payment_plan_id':
              setPaymentPlanId(setting.value);
              break;
            case 'tag':
              setTag(setting.value);
              break;
            case 'webhook_secret':
              setWebhookSecret(setting.value);
              break;
            case 'agreement_id':
              setAgreementId(setting.value);
              break;
            case 'url':
              setUrl(setting.value);
              break;
            case 'region_id':
              setRegionId(setting.value);
              break;
            case 'site_id':
              setSiteId(setting.value);
              break;
            case 'activation_code':
              setActivationCode(setting.value);
            case 'endpoint':
              setEndpoint(setting.value);
              break;
            case 'access_token':
              setAccessToken(setting.value);
            case 'club_key':
              setClubKey(setting.value);
              break;
            case 'schedule_id':
              setScheduleId(setting.value);
              break;
            case 'location_id':
              setLocationId(setting.value);
              break;
            case 'location_uuid':
              setLocationId(setting.value);
              break;
            case 'service_id':
              setServiceId(setting.value);
              break;
            case 'country_code':
              setCountryCode(setting.value);
              break;
            case 'studio_id':
              setStudioId(setting.value);
              break;
            case 'club_id':
              setClubID(setting.value);
              break;
            case 'network_id':
              setNetworkId(setting.value);
              break;
            case 'client_token':
              setClientToken(setting.value);
              break;
            default:
              break;
          }
        });
        setInitialProceed(true);
      }
    }
  }, [channel, locations]);

  return {
    state: {
      anchorEl,
      expandCard,
      selectedLocation,
      selectedLocationName,
      startDate,
      endDate,
      initialStartDate,
      initialEndDate,
      oneFitPartnerId,
      baseUrl,
      apiKey,
      webHookKey,
      venueId,
      livestream,
      venueUUID,
      clientId,
      clientSecret,
      clubId,
      paymentPlanId,
      tag,
      webhookSecret,
      agreementId,
      scheduleId,
      clubKey,
      initialProceed,
      isLoading,
      eversportsLoading,
      updateChannelLoading,
      locationsLoading,
      locationsError,
      error,
      eversportsError,
      virtuaGymError,
      virtuaGymLoading,
      virtuaGymV1Error,
      virtuaGymV1Loading,
      mindbodyError,
      mindbodyLoading,
      updateChannelError,
      channelUpdated,
      locations,
      url,
      regionId,
      siteId,
      activationCode,
      zingfitError,
      zingfitLoading,
      endpoint,
      accessToken,
      locationId,
      serviceId,
      countryCode,
      studioId,
      clubID,
      networkId,
      clientToken,
      resamaniaLoading,
      resamaniaError,
    },
    actions: {
      handleToggleDetails,
      handleOpenPopover,
      handleClosePopover,
      handleDateChange,
      clearEndDate,
      handleSelectItem,
      handleSave,
      setOneFitPartnerId,
      setBaseUrl,
      setApiKey,
      setWebHookKey,
      setVenueId,
      setLivestream,
      setVenueUUID,
      setClientId,
      setClientSecret,
      setClubId,
      setPaymentPlanId,
      setTag,
      setWebhookSecret,
      setAgreementId,
      setUrl,
      setRegionId,
      setSiteId,
      setActivationCode,
      setAccessToken,
      setEndpoint,
      setClubKey,
      setScheduleId,
      setLocationId,
      setServiceId,
      setCountryCode,
      setStudioId,
      setClubID,
      setNetworkId,
      setClientToken,
    },
  };
};
